import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MicVAD, utils as vadUtils } from '@ricky0123/vad-web';
import { Crown, Play } from 'lucide-react';
import Soundwave from './Soundwave';
import Modal from './Modal';
import config from '../config/config';
import { getVoiceCoachWsToken, startVoiceCoachHttp } from '../api/dataService';
import { useCoachQuota } from '../hooks/useCoachQuota';
import { getPreloadedVad, clearPreloadedVad } from '../utils/vadPreloader';
import { useAuth } from '../context/AuthContext';
import { useCoachUsage } from '../context/CoachUsageContext';
import { useNativeCaps } from '../context/NativeCapsContext';
import { useLocale } from '../context/LocaleContext';
import {
  hasKokoroAssetsInstalled,
  isFirstTimeLocalSetupReady,
  readOnDeviceTtsPreference,
  resolveVoiceCoachTtsMode,
  shouldGateFirstTimeLocalSetup,
  shouldProactivelyInitKokoro,
  shouldPromptForVoiceMode,
  writeOnDeviceTtsPreference,
} from '../utils/voiceCoachTtsMode';
import { buildRewardedUnlockCopy, REWARDED_UNLOCK_CONTEXTS } from '../utils/rewardedAds';

const PCM_BYTES_PER_SAMPLE = 2;
const KOKORO_UNLOAD_DEBOUNCE_MS = 90_000;

function normalizePublicUrl() {
  const raw = process.env.PUBLIC_URL;
  if (!raw) return '';
  return raw === '/' ? '' : raw.replace(/\/$/, '');
}

function toWsBaseUrl(httpBaseUrl) {
  if (!httpBaseUrl) return '';
  if (httpBaseUrl.startsWith('https://')) return httpBaseUrl.replace('https://', 'wss://');
  if (httpBaseUrl.startsWith('http://')) return httpBaseUrl.replace('http://', 'ws://');
  return httpBaseUrl;
}

function joinUrl(base, path) {
  const trimmedBase = base.endsWith('/') ? base.slice(0, -1) : base;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${trimmedBase}${normalizedPath}`;
}

function decodePcm16leToFloat32(arrayBuffer) {
  if (!arrayBuffer || arrayBuffer.byteLength < PCM_BYTES_PER_SAMPLE) {
    return new Float32Array(0);
  }
  const alignedByteLength = arrayBuffer.byteLength - (arrayBuffer.byteLength % PCM_BYTES_PER_SAMPLE);
  if (alignedByteLength <= 0) {
    return new Float32Array(0);
  }
  const alignedBuffer = alignedByteLength === arrayBuffer.byteLength
    ? arrayBuffer
    : arrayBuffer.slice(0, alignedByteLength);
  const pcm = new Int16Array(alignedBuffer);
  const float = new Float32Array(pcm.length);
  for (let i = 0; i < pcm.length; i += 1) {
    float[i] = pcm[i] / 32768;
  }
  return float;
}

async function decodeOggOpus(ctx, arrayBuffer) {
  try {
    return await ctx.decodeAudioData(arrayBuffer.slice(0));
  } catch (err) {
    console.warn('[AICoachOpenMic] Failed to decode OGG_OPUS chunk:', err);
    return null;
  }
}

function mergeUint8Chunks(chunks, totalBytes) {
  const merged = new Uint8Array(totalBytes);
  let offset = 0;
  for (const chunk of chunks) {
    const view = chunk instanceof Uint8Array ? chunk : new Uint8Array(chunk);
    merged.set(view, offset);
    offset += view.byteLength;
  }
  return merged;
}

function getMicApiDiagnostics() {
  const nav = typeof navigator !== 'undefined' ? navigator : null;
  const secureContext = typeof window !== 'undefined' ? window.isSecureContext : null;
  const protocol = typeof window !== 'undefined' ? window.location?.protocol || null : null;
  const host = typeof window !== 'undefined' ? window.location?.host || null : null;
  const hasMediaDevices = Boolean(nav?.mediaDevices);
  const hasGetUserMedia = typeof nav?.mediaDevices?.getUserMedia === 'function';
  return {
    secureContext,
    protocol,
    host,
    hasMediaDevices,
    hasGetUserMedia,
  };
}

const AICoachOpenMic = () => {
  const [sessionActive, setSessionActive] = useState(false);
  const [sessionStarting, setSessionStarting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [phase, setPhase] = useState('idle');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [transport, setTransport] = useState({ mode: 'ws', fallbackReason: null });
  const [error, setError] = useState(null);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [showVoiceModeModal, setShowVoiceModeModal] = useState(false);
  const [localVoiceSetupPhase, setLocalVoiceSetupPhase] = useState('idle');
  const [onDeviceVoicePreferred, setOnDeviceVoicePreferred] = useState(() => readOnDeviceTtsPreference());
  const [conversationContextId] = useState(() => `ctx-${Date.now()}-${Math.floor(Math.random() * 1e6)}`);

  const { user } = useAuth();
  const { t } = useLocale();
  const subscriptionPlan = user?.subscriptionPlan || 'free-tier';
  const { rewardedAds } = useCoachUsage();
  const {
    isNativeApp,
    caps: nativeCaps,
    kokoroReady,
    kokoroWarm,
    kokoroError,
    kokoroPlaybackActive,
    requestCaps,
    postToNative,
  } = useNativeCaps();

  const {
    coachLimitReached,
    limitMinutes,
    isLoading: isLoadingUsage,
    refreshCoachUsage,
    setCoachUsage,
    markLimitReached,
  } = useCoachQuota();

  const wsRef = useRef(null);
  const vadRef = useRef(null);
  const audioContextRef = useRef(null);
  const scheduledAtRef = useRef(0);
  const scheduledNodesRef = useRef([]);
  const bufferedPcmChunksRef = useRef([]);
  const bufferedPcmBytesRef = useRef(0);
  const currentGenerationSeqRef = useRef(null);
  const serverPhaseRef = useRef('idle');
  const speakingRef = useRef(false);
  const maxUtteranceTimerRef = useRef(null);
  const sessionActiveRef = useRef(false);
  const sessionStartingRef = useRef(false);
  const vadReadyRef = useRef(false);
  const sessionRunIdRef = useRef(0);
  const audioElementRef = useRef(null);
  const stopSessionRef = useRef(null);
  const audioEncodingRef = useRef('ogg_opus');
  const ttsModeRef = useRef('server_pcm');
  const pendingNativeSegmentsRef = useRef([]);
  const nativeSpeakRequestedRef = useRef(false);
  const kokoroReadyRef = useRef(Boolean(kokoroReady));
  const kokoroInitRequestedRef = useRef(false);
  const proactiveKokoroInitAttemptedRef = useRef(false);
  const wsReconnectInFlightRef = useRef(false);
  const nativeSpeakGuardTimerRef = useRef(null);
  const nativeUnloadTimerRef = useRef(null);
  const pendingVoiceModeStartRef = useRef(null);
  const firstTimeLocalSetupActive = localVoiceSetupPhase === 'installing';
  const firstTimeLocalSetupReady = localVoiceSetupPhase === 'ready';

  const emitTtsTrace = useCallback(
    (phase, details = {}) => {
      const payload = {
        phase,
        at: new Date().toISOString(),
        ...details,
      };
      console.log('[AICoachOpenMic][tts-trace]', payload);
      if (isNativeApp) {
        postToNative({
          type: 'FORZA_DEBUG_TRACE',
          scope: 'voice_coach_tts',
          payload,
        });
      }
    },
    [isNativeApp, postToNative]
  );

  useEffect(() => {
    kokoroReadyRef.current = Boolean(kokoroReady);
    if (kokoroReady) {
      kokoroInitRequestedRef.current = false;
    }
  }, [kokoroReady]);

  const deviceInfo = useMemo(() => {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      vendor: navigator.vendor,
    };
  }, []);

  const downlinkSampleRateRef = useRef(24000);
  const uplinkSampleRate = 16000;

  const persistOnDeviceVoicePreference = useCallback((enabled) => {
    writeOnDeviceTtsPreference(enabled);
    setOnDeviceVoicePreferred(Boolean(enabled));
  }, []);

  const kokoroAssetsInstalled = useMemo(() => {
    return hasKokoroAssetsInstalled(nativeCaps, kokoroReady);
  }, [nativeCaps, kokoroReady]);

  const shouldPromptForInitialVoiceChoice = useMemo(() => {
    return shouldPromptForVoiceMode({
      isNativeApp,
      kokoroAvailable: nativeCaps?.kokoroTts,
      caps: nativeCaps,
      kokoroReady,
    });
  }, [isNativeApp, nativeCaps, kokoroReady]);

  const resolveSessionTtsMode = useCallback(
    (requestedMode = null) => {
      return resolveVoiceCoachTtsMode({
        requestedMode,
        isNativeApp,
        kokoroAvailable: nativeCaps?.kokoroTts,
        onDevicePreferenceEnabled: onDeviceVoicePreferred,
      });
    },
    [isNativeApp, nativeCaps?.kokoroTts, onDeviceVoicePreferred]
  );

  const shouldWarmKokoroProactively = useMemo(() => {
    return shouldProactivelyInitKokoro({
      isNativeApp,
      kokoroAvailable: nativeCaps?.kokoroTts,
      caps: nativeCaps,
      kokoroReady,
      requestedMode: null,
      onDevicePreferenceEnabled: onDeviceVoicePreferred,
    });
  }, [isNativeApp, nativeCaps, kokoroReady, onDeviceVoicePreferred]);

  const vadAssetConfig = useMemo(() => {
    const base = normalizePublicUrl();
    return {
      baseAssetPath: `${base}/voice/vad/`,
      onnxWASMBasePath: `${base}/voice/ort/`,
    };
  }, []);

  const stopHttpAudio = useCallback(() => {
    if (!audioElementRef.current) return;
    try {
      audioElementRef.current.pause();
      audioElementRef.current.currentTime = 0;
    } catch {
      // ignore
    }
    audioElementRef.current = null;
  }, []);

  const stopPlayback = useCallback(() => {
    stopHttpAudio();

    const nodes = scheduledNodesRef.current;
    for (const node of nodes) {
      try {
        node.stop(0);
      } catch {
        // ignore
      }
      try {
        node.disconnect();
      } catch {
        // ignore
      }
    }
    scheduledNodesRef.current = [];
    bufferedPcmChunksRef.current = [];
    bufferedPcmBytesRef.current = 0;
    speakingRef.current = false;
    setIsSpeaking(false);

    const ctx = audioContextRef.current;
    if (ctx) {
      scheduledAtRef.current = ctx.currentTime;
    } else {
      scheduledAtRef.current = 0;
    }
  }, [stopHttpAudio]);

  const stopNativePlayback = useCallback(() => {
    if (ttsModeRef.current !== 'native_text') {
      return;
    }
    if (nativeSpeakGuardTimerRef.current) {
      clearTimeout(nativeSpeakGuardTimerRef.current);
      nativeSpeakGuardTimerRef.current = null;
    }
    pendingNativeSegmentsRef.current = [];
    nativeSpeakRequestedRef.current = false;
    emitTtsTrace('kokoro_stop_sent', { transport: 'ws' });
    postToNative({ type: 'FORZA_KOKORO_STOP' });
  }, [postToNative, emitTtsTrace]);

  const ensureAudioContext = useCallback(async () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
      scheduledAtRef.current = audioContextRef.current.currentTime;
      return;
    }
    if (audioContextRef.current.state === 'suspended') {
      await audioContextRef.current.resume();
    }
  }, []);

  const sendWsJson = useCallback((payload) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) return;
    try {
      ws.send(JSON.stringify(payload));
    } catch {
      // ignore
    }
  }, []);

  const flushBufferedAudio = useCallback(async () => {
    const ctx = audioContextRef.current;
    if (!ctx) return;
    const generationSeq = currentGenerationSeqRef.current;
    if (!generationSeq) return;

    const totalBytes = bufferedPcmBytesRef.current;
    if (!totalBytes) return;

    const merged = mergeUint8Chunks(bufferedPcmChunksRef.current, totalBytes);
    bufferedPcmChunksRef.current = [];
    bufferedPcmBytesRef.current = 0;

    const encoding = audioEncodingRef.current;
    let audioBuffer;

    if (encoding === 'ogg_opus') {
      // Decode OGG_OPUS using browser's native decoder
      audioBuffer = await decodeOggOpus(ctx, merged.buffer);
      if (!audioBuffer) return;
    } else {
      // Fallback to raw PCM decoding (LINEAR16)
      const sampleRate = downlinkSampleRateRef.current || 24000;
      const pcmBuffer = merged.buffer.slice(merged.byteOffset, merged.byteOffset + merged.byteLength);
      const float = decodePcm16leToFloat32(pcmBuffer);
      if (float.length === 0) return;
      audioBuffer = ctx.createBuffer(1, float.length, sampleRate);
      audioBuffer.copyToChannel(float, 0);
    }

    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(ctx.destination);

    const now = ctx.currentTime;
    const startAt = Math.max(now + 0.02, scheduledAtRef.current);
    scheduledAtRef.current = startAt + audioBuffer.duration;
    scheduledNodesRef.current.push(source);
    speakingRef.current = true;
    setIsSpeaking(true);
    source.start(startAt);

    source.onended = () => {
      const list = scheduledNodesRef.current;
      scheduledNodesRef.current = list.filter((n) => n !== source);
      if (scheduledNodesRef.current.length === 0 && bufferedPcmBytesRef.current === 0) {
        speakingRef.current = false;
        setIsSpeaking(false);
      }
    };
  }, []);

  const enqueueAudioFrame = useCallback(
    (generationSeq, audioArrayBuffer) => {
      if (!audioArrayBuffer || audioArrayBuffer.byteLength === 0) return;
      if (generationSeq !== currentGenerationSeqRef.current) return;

      bufferedPcmChunksRef.current.push(new Uint8Array(audioArrayBuffer));
      bufferedPcmBytesRef.current += audioArrayBuffer.byteLength;

      // For OGG_OPUS, we need larger buffers as decodeAudioData expects complete packets
      // For PCM, smaller chunks are fine
      const encoding = audioEncodingRef.current;
      const flushBytes = encoding === 'ogg_opus' ? 8000 : 2400;
      if (bufferedPcmBytesRef.current >= flushBytes) {
        void flushBufferedAudio();
      }
    },
    [flushBufferedAudio]
  );

  const playHttpAudio = useCallback(
    async (src) => {
      stopPlayback();
      if (!src) return;
      try {
        const audio = new Audio();
        audio.src = src;
        audio.load();
        audioElementRef.current = audio;
        speakingRef.current = true;
        setIsSpeaking(true);
        await audio.play();
        audio.addEventListener('ended', () => {
          speakingRef.current = false;
          setIsSpeaking(false);
        });
      } catch {
        speakingRef.current = false;
        setIsSpeaking(false);
      }
    },
    [stopPlayback]
  );

  const handleSpeechStart = useCallback(() => {
    if (!sessionActiveRef.current) return;
    // Only stop playback if speaking, but don't trigger barge-in yet
    // Barge-in will be triggered on handleSpeechRealStart for less sensitivity
    if (speakingRef.current) {
      stopPlayback();
    }
    if (ttsModeRef.current === 'native_text' && (kokoroPlaybackActive || nativeSpeakRequestedRef.current)) {
      emitTtsTrace('vad_speech_start_ignored_during_kokoro', {
        transport: 'ws',
        kokoroPlaybackActive,
        nativeSpeakRequested: nativeSpeakRequestedRef.current,
      });
      return;
    }
  }, [kokoroPlaybackActive, stopPlayback, emitTtsTrace]);

  const handleSpeechRealStart = useCallback(async () => {
    if (!sessionActiveRef.current) return;

    if (ttsModeRef.current === 'native_text' && (kokoroPlaybackActive || nativeSpeakRequestedRef.current)) {
      emitTtsTrace('vad_real_start_ignored_during_kokoro', {
        transport: 'ws',
        phase: serverPhaseRef.current,
        kokoroPlaybackActive,
        nativeSpeakRequested: nativeSpeakRequestedRef.current,
      });
      return;
    }

    // Trigger barge-in on confirmed real speech (more reliable than handleSpeechStart)
    const serverPhase = serverPhaseRef.current;
    if (serverPhase && serverPhase !== 'listening') {
      stopPlayback();
      stopNativePlayback();
      sendWsJson({ type: 'barge_in', reason: 'user_speaking' });
      emitTtsTrace('ws_barge_in_sent', {
        reason: 'user_speaking',
        transport: 'ws',
        serverPhase,
      });
    } else if (speakingRef.current) {
      stopPlayback();
      stopNativePlayback();
      sendWsJson({ type: 'barge_in', reason: 'barge_in' });
      emitTtsTrace('ws_barge_in_sent', {
        reason: 'barge_in',
        transport: 'ws',
        serverPhase,
      });
    }

    if (maxUtteranceTimerRef.current) {
      clearTimeout(maxUtteranceTimerRef.current);
    }
    maxUtteranceTimerRef.current = setTimeout(async () => {
      const vad = vadRef.current;
      if (!vad || !sessionActiveRef.current) return;
      try {
        await vad.pause();
        await vad.start();
      } catch {
        // ignore
      }
    }, 180000);
  }, [sendWsJson, stopNativePlayback, stopPlayback, kokoroPlaybackActive, emitTtsTrace]);

  const sendUtteranceOverHttp = useCallback(
    async (wavArrayBuffer, durationMs, utteranceId, fallbackReason) => {
      setTransport({ mode: 'http', fallbackReason });
      try {
        const wavBlob = new Blob([wavArrayBuffer], { type: 'audio/wav' });
        const wavFile = new File([wavBlob], 'utterance.wav', { type: 'audio/wav' });
        const response = await startVoiceCoachHttp({
          audioBlob: wavFile,
          durationMs,
          utteranceId,
          deviceInfo,
          conversationContextId,
          fallbackReason,
        });

        const data = response?.data || {};
        const debugPayload = data?.ttsDebug || {
          utteranceId: utteranceId || null,
          negotiatedMode: 'server_pcm',
          transport: 'http',
          provider: data?.tts?.urlOrBytes ? 'google_http' : 'none',
        };
        console.log('[AICoachOpenMic][tts-debug]', debugPayload);
        emitTtsTrace('http_tts_debug', debugPayload);
        if (data.ignored) {
          return;
        }

        const usage = data.usage || null;
        if (usage) {
          setCoachUsage({
            usageSeconds: usage.monthlySeconds ?? 0,
            limitMinutes: usage.limitMinutes ?? limitMinutes ?? 0,
          });
        }

        const tts = data.tts || {};
        if (tts.urlOrBytes) {
          await playHttpAudio(tts.urlOrBytes);
        }
      } catch (err) {
        if (err?.response?.status === 429) {
          markLimitReached();
          setError(t('aiCoach.monthlyLimitReached', 'Monthly AI Coach limit reached.'));
          sessionActiveRef.current = false;
          setSessionActive(false);
          return;
        }
        setError(t('aiCoach.fallbackFailed', 'Voice coach fallback failed. Please try again.'));
      }
    },
    [
      conversationContextId,
      deviceInfo,
      limitMinutes,
      markLimitReached,
      playHttpAudio,
      setCoachUsage,
      emitTtsTrace,
      t,
    ]
  );

  useEffect(() => {
    if (!shouldWarmKokoroProactively) {
      return;
    }
    if (sessionActiveRef.current || sessionStartingRef.current || firstTimeLocalSetupActive) {
      return;
    }
    if (kokoroReady || kokoroInitRequestedRef.current || proactiveKokoroInitAttemptedRef.current) {
      return;
    }

    proactiveKokoroInitAttemptedRef.current = true;
    kokoroInitRequestedRef.current = true;
    emitTtsTrace('kokoro_proactive_init_requested', {
      transport: 'ws',
      reason: 'installed_native_default',
    });
    postToNative({
      type: 'FORZA_KOKORO_INIT',
      voice: 'af_heart',
      language: 'en-US',
    });
  }, [emitTtsTrace, firstTimeLocalSetupActive, kokoroReady, postToNative, shouldWarmKokoroProactively]);

  const handleSpeechEnd = useCallback(
    async (audio) => {
      if (!sessionActiveRef.current || !vadReadyRef.current || !audio) return;

      if (maxUtteranceTimerRef.current) {
        clearTimeout(maxUtteranceTimerRef.current);
        maxUtteranceTimerRef.current = null;
      }

      const wavArrayBuffer = vadUtils.encodeWAV(audio, 1, uplinkSampleRate, 1, 16);
      const durationMs = Math.round((audio.length / uplinkSampleRate) * 1000);
      const utteranceId = `utt-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
      if (durationMs < 250) {
        return;
      }

      if (
        isNativeApp &&
        ttsModeRef.current === 'native_text' &&
        !kokoroReadyRef.current &&
        !kokoroInitRequestedRef.current
      ) {
        kokoroInitRequestedRef.current = true;
        emitTtsTrace('kokoro_init_requested_after_first_utterance', {
          transport: 'ws',
          utteranceId,
        });
        postToNative({
          type: 'FORZA_KOKORO_INIT',
          voice: 'af_heart',
          language: 'en-US',
        });
      }

      const ws = wsRef.current;
      if (!ws || ws.readyState !== 1) {
        await sendUtteranceOverHttp(
          wavArrayBuffer,
          durationMs,
          utteranceId,
          transport.fallbackReason || 'ws_unavailable'
        );
        return;
      }

      sendWsJson({
        type: 'start_utterance',
        utteranceId,
        ttsMode: ttsModeRef.current || 'server_pcm',
      });
      emitTtsTrace('ws_start_utterance_sent', {
        utteranceId,
        requestedMode: ttsModeRef.current || 'server_pcm',
        transport: 'ws',
      });
      try {
        ws.send(wavArrayBuffer);
      } catch {
        await sendUtteranceOverHttp(wavArrayBuffer, durationMs, utteranceId, 'ws_send_failed');
        return;
      }
      sendWsJson({ type: 'end_utterance', utteranceId });
    },
    [emitTtsTrace, isNativeApp, postToNative, sendUtteranceOverHttp, sendWsJson, transport.fallbackReason]
  );

  const startVAD = useCallback(async () => {
    if (vadRef.current) {
      console.log('[AICoachOpenMic] VAD already exists, skipping start');
      vadReadyRef.current = true;
      return;
    }

    console.log('[AICoachOpenMic] Starting VAD...');
    vadReadyRef.current = false;

    // Clear any preloaded VAD - we'll create a fresh one with correct callbacks
    // The preload still benefits us by warming up the model in browser cache
    const preloaded = getPreloadedVad();
    if (preloaded) {
      console.log('[AICoachOpenMic] Destroying preloaded VAD, will create fresh instance');
      try {
        await preloaded.destroy();
      } catch {
        // ignore
      }
      clearPreloadedVad();
    }

    // Always create fresh VAD with correct callbacks bound
    console.log('[AICoachOpenMic] Creating fresh VAD instance...');
    const vad = await MicVAD.new({
      model: 'v5',
      startOnLoad: false,
      processorType: 'auto',
      redemptionMs: 700,
      preSpeechPadMs: 250,
      minSpeechMs: 400,
      submitUserSpeechOnPause: true,
      baseAssetPath: vadAssetConfig.baseAssetPath,
      onnxWASMBasePath: vadAssetConfig.onnxWASMBasePath,
      onSpeechStart: handleSpeechStart,
      onSpeechRealStart: handleSpeechRealStart,
      onSpeechEnd: handleSpeechEnd,
    });

    console.log('[AICoachOpenMic] VAD created, starting...');
    await vad.start();
    vadRef.current = vad;
    vadReadyRef.current = true;
    console.log('[AICoachOpenMic] VAD started successfully');
  }, [handleSpeechEnd, handleSpeechRealStart, handleSpeechStart, vadAssetConfig]);

  const stopVAD = useCallback(async () => {
    vadReadyRef.current = false;
    if (maxUtteranceTimerRef.current) {
      clearTimeout(maxUtteranceTimerRef.current);
      maxUtteranceTimerRef.current = null;
    }

    const vad = vadRef.current;
    vadRef.current = null;
    if (vad) {
      console.log('[AICoachOpenMic] Destroying VAD...');
      try {
        await vad.destroy();
      } catch {
        // ignore
      }
    }
  }, []);

  const connectWs = useCallback(
    async (sessionToken) => {
      const wsBase = toWsBaseUrl(config.api.baseUrl);
      const wsUrl = joinUrl(wsBase, `/voice/ws?sessionToken=${encodeURIComponent(sessionToken)}`);
      const ws = new WebSocket(wsUrl);
      const helloRequestedTtsMode = ttsModeRef.current || 'server_pcm';
      let helloAckTimer = null;
      const clearHelloAckTimer = () => {
        if (helloAckTimer) {
          clearTimeout(helloAckTimer);
          helloAckTimer = null;
        }
      };
      ws.binaryType = 'arraybuffer';
      wsRef.current = ws;
      setConnectionStatus('connecting');
      setTransport({ mode: 'ws', fallbackReason: null });

      // Promise to wait for WebSocket readiness without blocking VAD startup.
      let resolveWsReady;
      const wsReadyPromise = new Promise((resolve) => {
        resolveWsReady = resolve;
        // Timeout fallback - don't block forever
        setTimeout(() => resolve(), 5000);
      });

      ws.onopen = () => {
        setConnectionStatus('connected');
        sendWsJson({
          type: 'client_hello',
          conversationContextId,
          deviceInfo,
          ttsMode: helloRequestedTtsMode,
        });
        console.log('[AICoachOpenMic] client_hello sent', { requestedTtsMode: helloRequestedTtsMode, isNativeApp, nativeCaps });
        emitTtsTrace('ws_client_hello_sent', {
          negotiatedMode: helloRequestedTtsMode,
          transport: 'ws',
        });
        helloAckTimer = setTimeout(() => {
          emitTtsTrace('ws_client_hello_ack_timeout', {
            requestedMode: helloRequestedTtsMode,
            transport: 'ws',
          });
          const retryMode = ttsModeRef.current || helloRequestedTtsMode;
          sendWsJson({
            type: 'client_hello',
            conversationContextId,
            deviceInfo,
            ttsMode: retryMode,
          });
          emitTtsTrace('ws_client_hello_retry_sent', {
            requestedMode: retryMode,
            transport: 'ws',
          });
        }, 4000);
        resolveWsReady();
      };

      ws.onmessage = async (event) => {
        if (typeof event.data === 'string') {
          let message;
          try {
            message = JSON.parse(event.data);
          } catch {
            return;
          }

          if (message?.type === 'status') {
            const next = message.phase;
            serverPhaseRef.current = next;
            setPhase(next);
            return;
          }

          if (message?.type === 'tts_start') {
            currentGenerationSeqRef.current = message.generationSeq;
            downlinkSampleRateRef.current = message?.audio?.sampleRateHz || 24000;
            // Parse encoding from server (e.g., 'ogg_opus', 'pcm_s16le')
            const serverEncoding = message?.audio?.encoding?.toLowerCase() || 'ogg_opus';
            audioEncodingRef.current = serverEncoding.includes('ogg') ? 'ogg_opus' : 'pcm';
            stopPlayback();
            return;
          }

          if (message?.type === 'client_hello_ack') {
            clearHelloAckTimer();
            const ackMode = typeof message.ttsMode === 'string' ? message.ttsMode : null;
            if (ackMode === 'native_text' || ackMode === 'server_pcm') {
              ttsModeRef.current = ackMode;
            }
            console.log('[AICoachOpenMic] client_hello_ack', {
              ackMode: message?.ttsMode,
              requestedMode: helloRequestedTtsMode,
              effectiveMode: ttsModeRef.current,
              rawRequestedMode: message?.rawTtsMode || null,
            });
            emitTtsTrace('ws_client_hello_ack', {
              requestedMode: helloRequestedTtsMode,
              negotiatedMode: ttsModeRef.current,
              transport: 'ws',
              rawRequestedMode: message?.rawTtsMode || null,
            });
            return;
          }

          if (message?.type === 'tts_debug') {
            const payload = {
              utteranceId: message?.utteranceId || null,
              generationSeq: message?.generationSeq || null,
              negotiatedMode: message?.negotiatedMode || null,
              transport: message?.transport || 'ws',
              provider: message?.provider || 'none',
              segmentCount: Number.isFinite(message?.segmentCount) ? message.segmentCount : null,
            };
            console.log('[AICoachOpenMic][tts-debug]', payload);
            emitTtsTrace('ws_tts_debug', payload);
            return;
          }

          if (message?.type === 'tts_mode_applied') {
            emitTtsTrace('ws_tts_mode_applied', {
              negotiatedMode: message?.ttsMode || null,
              rawRequestedMode: message?.rawTtsMode || null,
              source: message?.source || null,
              transport: 'ws',
            });
            return;
          }

          if (message?.type === 'assistant_text_segment') {
            if (ttsModeRef.current !== 'native_text') {
              return;
            }
            const text = typeof message.text === 'string' ? message.text : '';
            const segmentId = typeof message.segmentId === 'string' ? message.segmentId : `seg-${Date.now()}`;
            if (!text) return;

            if (kokoroReadyRef.current) {
              const vad = vadRef.current;
              if (vad) {
                try {
                  await vad.pause();
                  emitTtsTrace('vad_paused_before_kokoro_segment', {
                    transport: 'ws',
                    segmentId,
                  });
                } catch {
                  // ignore
                }
              }
              nativeSpeakRequestedRef.current = true;
              emitTtsTrace('kokoro_segment_dispatched', {
                transport: 'ws',
                segmentId,
                textChars: text.length,
              });
              postToNative({ type: 'FORZA_KOKORO_SPEAK', text, utteranceId: segmentId });
              return;
            }

            const queue = pendingNativeSegmentsRef.current;
            queue.push({ text, utteranceId: segmentId });
            while (queue.length > 5) {
              queue.shift();
            }
            return;
          }

          if (message?.type === 'tts_end') {
            void flushBufferedAudio();
            return;
          }

          if (message?.type === 'tts_fallback') {
            if (message.base64 && message.mimeType) {
              const src = `data:${message.mimeType};base64,${message.base64}`;
              await playHttpAudio(src);
            }
            return;
          }

          if (message?.type === 'generation_canceled') {
            if (message.generationSeq && message.generationSeq === currentGenerationSeqRef.current) {
              stopPlayback();
              stopNativePlayback();
              currentGenerationSeqRef.current = null;
            }
            return;
          }

          if (message?.type === 'usage') {
            setCoachUsage({
              usageSeconds: message.monthlySeconds ?? 0,
              limitMinutes: message.limitMinutes ?? limitMinutes ?? 0,
            });
            return;
          }

          if (message?.type === 'quota_exceeded') {
            markLimitReached();
            setError(message.message || t('aiCoach.monthlyLimitReached', 'Monthly AI Coach limit reached.'));
            void stopSessionRef.current?.();
            return;
          }

          if (message?.type === 'quota_reached') {
            markLimitReached();
            setError(message.message || t('aiCoach.quotaLocked', 'Voice Coach locked.'));

            sessionActiveRef.current = false;
            setSessionActive(false);
            setPhase('idle');
            serverPhaseRef.current = 'idle';

            stopNativePlayback();
            await stopVAD();
            return;
          }

          if (message?.type === 'idle_timeout') {
            setError(t('aiCoach.idleTimeout', 'Session closed due to inactivity.'));
            void stopSessionRef.current?.();
            return;
          }

          if (message?.type === 'utterance_ignored') {
            return;
          }

          if (message?.type === 'error') {
            setError(message.error || t('aiCoach.genericError', 'Voice coach error.'));
            return;
          }

          return;
        }

        const buffer = event.data;
        if (!(buffer instanceof ArrayBuffer) || buffer.byteLength <= 4) return;

        const view = new DataView(buffer);
        const generationSeq = view.getUint32(0, true);
        const audioBuffer = buffer.slice(4);
        enqueueAudioFrame(generationSeq, audioBuffer);
      };

      ws.onclose = (evt) => {
        clearHelloAckTimer();
        wsRef.current = null;
        setConnectionStatus('disconnected');

        const code = evt?.code;
        const shouldStop =
          code === 4002 || // quota exceeded
          code === 4001 || // idle timeout
          code === 4000 || // session replaced
          code === 1008; // policy violation

        if (shouldStop) {
          stopPlayback();
          stopNativePlayback();
          void stopVAD();
          sessionActiveRef.current = false;
          setSessionActive(false);
          return;
        }

        if (sessionActiveRef.current) {
          emitTtsTrace('ws_closed_fallback_http', {
            transport: 'http',
            fallbackReason: `ws_closed_${code || 'unknown'}`,
            wsCloseCode: code || null,
          });
          setTransport({ mode: 'http', fallbackReason: `ws_closed_${code || 'unknown'}` });
        }
      };

      ws.onerror = () => {
        clearHelloAckTimer();
        if (!sessionActiveRef.current) return;
        emitTtsTrace('ws_error_fallback_http', {
          transport: 'http',
          fallbackReason: 'ws_error',
        });
        setTransport({ mode: 'http', fallbackReason: 'ws_error' });
        resolveWsReady(); // Resolve even on error so VAD can start with HTTP fallback
      };

      return wsReadyPromise;
    },
    [
      conversationContextId,
      deviceInfo,
      enqueueAudioFrame,
      flushBufferedAudio,
      limitMinutes,
      markLimitReached,
      playHttpAudio,
      sendWsJson,
      setCoachUsage,
      stopNativePlayback,
      stopPlayback,
      stopVAD,
      kokoroReady,
      postToNative,
      emitTtsTrace,
      t,
    ]
  );

  useEffect(() => {
    if (nativeSpeakGuardTimerRef.current) {
      clearTimeout(nativeSpeakGuardTimerRef.current);
      nativeSpeakGuardTimerRef.current = null;
    }
    if (kokoroPlaybackActive) {
      emitTtsTrace('kokoro_guard_playback_active', { transport: 'ws' });
      return;
    }
    if (!nativeSpeakRequestedRef.current) {
      return;
    }
    nativeSpeakGuardTimerRef.current = setTimeout(async () => {
      nativeSpeakGuardTimerRef.current = null;
      if (!sessionActiveRef.current) {
        nativeSpeakRequestedRef.current = false;
        return;
      }
      if (kokoroPlaybackActive) {
        return;
      }
      nativeSpeakRequestedRef.current = false;
      emitTtsTrace('kokoro_guard_cleared_no_more_playback', { transport: 'ws' });
      const vad = vadRef.current;
      if (!vad) {
        return;
      }
      try {
        await vad.start();
        emitTtsTrace('vad_resumed_after_kokoro_guard_clear', { transport: 'ws' });
      } catch {
        // ignore
      }
    }, 5000);
  }, [kokoroPlaybackActive, emitTtsTrace]);

  useEffect(() => {
    if (!sessionActiveRef.current) {
      return;
    }
    if (ttsModeRef.current !== 'native_text') {
      return;
    }
    const vad = vadRef.current;
    if (!vad) {
      return;
    }

    const syncVad = async () => {
      try {
        if (kokoroPlaybackActive || nativeSpeakRequestedRef.current) {
          emitTtsTrace('vad_paused_for_kokoro_playback', { transport: 'ws' });
          await vad.pause();
          return;
        }
        emitTtsTrace('vad_resumed_after_kokoro_playback', { transport: 'ws' });
        await vad.start();
      } catch {
        // ignore
      }
    };

    void syncVad();
  }, [kokoroPlaybackActive, emitTtsTrace]);

  const reconnectToServerTts = useCallback(async (reason = 'manual') => {
    if (wsReconnectInFlightRef.current) {
      return;
    }
    wsReconnectInFlightRef.current = true;

    try {
      emitTtsTrace('forced_server_pcm_reconnect', {
        reason,
        negotiatedMode: 'server_pcm',
        transport: 'ws',
      });
      ttsModeRef.current = 'server_pcm';
      stopNativePlayback();
      stopPlayback();

      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch {
          // ignore
        }
        wsRef.current = null;
      }

      const tokenResponse = await getVoiceCoachWsToken();
      const sessionToken = tokenResponse?.data?.sessionToken;
      if (sessionToken) {
        await connectWs(sessionToken);
      } else {
        emitTtsTrace('reconnect_missing_ws_token', {
          transport: 'http',
          fallbackReason: 'missing_ws_token',
          reason,
        });
        setTransport({ mode: 'http', fallbackReason: 'missing_ws_token' });
      }
    } finally {
      wsReconnectInFlightRef.current = false;
    }
  }, [connectWs, emitTtsTrace, stopNativePlayback, stopPlayback]);

  useEffect(() => {
    if (!kokoroError) {
      return;
    }
    kokoroInitRequestedRef.current = false;
  }, [kokoroError]);

  useEffect(() => {
    if (!sessionActiveRef.current) {
      return;
    }
    if (ttsModeRef.current !== 'native_text') {
      return;
    }
    if (!kokoroError) {
      return;
    }

    if (kokoroError !== 'user_skipped') {
      setError(t('aiCoach.kokoroFallback', 'Kokoro failed. Falling back to server TTS.'));
    }
    console.warn('[AICoachOpenMic] kokoroError; reconnecting to server TTS', {
      kokoroError,
      modeBeforeFallback: ttsModeRef.current,
    });
    emitTtsTrace('kokoro_error_detected', {
      kokoroError,
      modeBeforeFallback: ttsModeRef.current,
    });
    void reconnectToServerTts('kokoro_error');
  }, [kokoroError, reconnectToServerTts, emitTtsTrace]);

  useEffect(() => {
    if (!kokoroReady) {
      return;
    }

    if (ttsModeRef.current !== 'native_text') {
      return;
    }

    const queue = pendingNativeSegmentsRef.current;
    if (!queue.length) {
      return;
    }

    for (const item of queue) {
      postToNative({ type: 'FORZA_KOKORO_SPEAK', text: item.text, utteranceId: item.utteranceId });
      nativeSpeakRequestedRef.current = true;
    }
    pendingNativeSegmentsRef.current = [];
  }, [kokoroReady, postToNative]);

  const stopSession = useCallback(async () => {
    const shouldDebounceUnload = Boolean(ttsModeRef.current === 'native_text' || kokoroReadyRef.current);
    if (nativeUnloadTimerRef.current) {
      clearTimeout(nativeUnloadTimerRef.current);
      nativeUnloadTimerRef.current = null;
    }

    sessionRunIdRef.current += 1;
    sessionStartingRef.current = false;
    sessionActiveRef.current = false;
    vadReadyRef.current = false;
    kokoroInitRequestedRef.current = false;
    pendingVoiceModeStartRef.current = null;
    setSessionStarting(false);
    setSessionActive(false);
    setShowVoiceModeModal(false);
    setLocalVoiceSetupPhase('idle');
    setPhase('idle');
    serverPhaseRef.current = 'idle';
    setError(null);
    setConnectionStatus('disconnected');

    stopPlayback();
    stopNativePlayback();
    await stopVAD();

    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch {
        // ignore
      }
      wsRef.current = null;
    }

    if (audioContextRef.current) {
      try {
        await audioContextRef.current.close();
      } catch {
        // ignore
      }
      audioContextRef.current = null;
    }

    if (shouldDebounceUnload) {
      emitTtsTrace('kokoro_unload_scheduled', { transport: 'ws', debounceMs: KOKORO_UNLOAD_DEBOUNCE_MS });
      nativeUnloadTimerRef.current = setTimeout(() => {
        nativeUnloadTimerRef.current = null;
        emitTtsTrace('kokoro_unload_sent', { transport: 'ws', debounceMs: KOKORO_UNLOAD_DEBOUNCE_MS });
        postToNative({ type: 'FORZA_KOKORO_UNLOAD' });
      }, KOKORO_UNLOAD_DEBOUNCE_MS);
    }
  }, [emitTtsTrace, postToNative, stopNativePlayback, stopPlayback, stopVAD]);

  useEffect(() => {
    stopSessionRef.current = stopSession;
  }, [stopSession]);

  const startSession = useCallback(async (requestedMode = null) => {
    setError(null);
    if (nativeUnloadTimerRef.current) {
      clearTimeout(nativeUnloadTimerRef.current);
      nativeUnloadTimerRef.current = null;
      emitTtsTrace('kokoro_unload_canceled', {
        transport: 'ws',
        reason: 'session_restarted_within_debounce',
        debounceMs: KOKORO_UNLOAD_DEBOUNCE_MS,
      });
    }
    if (sessionActiveRef.current || sessionStartingRef.current) return;

    sessionStartingRef.current = true;
    setSessionStarting(true);
    setPhase('starting');
    serverPhaseRef.current = 'starting';
    const startupRunId = sessionRunIdRef.current + 1;
    sessionRunIdRef.current = startupRunId;

    try {
      const micDiag = getMicApiDiagnostics();
      if (!micDiag.hasGetUserMedia) {
        const micApiError = new Error('Browser microphone API unavailable');
        micApiError.name = 'MicApiUnavailableError';
        micApiError.code = 'MIC_API_UNAVAILABLE';
        micApiError.diag = micDiag;
        throw micApiError;
      }

      const latestUsage = await refreshCoachUsage();
      const effectiveUsageSeconds = latestUsage?.usageSeconds;
      const effectiveLimitMinutes = latestUsage?.limitMinutes;

      if (
        typeof effectiveLimitMinutes === 'number' &&
        effectiveLimitMinutes !== -1 &&
        typeof effectiveUsageSeconds === 'number' &&
        effectiveUsageSeconds >= effectiveLimitMinutes * 60
      ) {
        sessionStartingRef.current = false;
        setSessionStarting(false);
        setPhase('idle');
        serverPhaseRef.current = 'idle';
        markLimitReached();
        return;
      }

      await ensureAudioContext();

      ttsModeRef.current = resolveSessionTtsMode(requestedMode);
      const canUseNativeTts = ttsModeRef.current === 'native_text';
      console.log('[AICoachOpenMic] startSession TTS mode decision', {
        isNativeApp,
        nativeCaps,
        canUseNativeTts,
        requestedMode,
        onDeviceVoicePreferred,
        selectedMode: ttsModeRef.current,
      });
      emitTtsTrace('start_mode_decision', {
        requestedMode,
        negotiatedMode: ttsModeRef.current,
        transport: 'ws',
        canUseNativeTts,
      });

      await startVAD();

      if (!sessionStartingRef.current || sessionRunIdRef.current !== startupRunId) {
        return;
      }
      sessionActiveRef.current = true;
      sessionStartingRef.current = false;
      setSessionActive(true);
      setSessionStarting(false);
      setPhase('listening');
      serverPhaseRef.current = 'listening';

      void (async () => {
        try {
          if (!sessionActiveRef.current || sessionRunIdRef.current !== startupRunId) {
            return;
          }

          if (isNativeApp) {
            requestCaps();
          }

          const tokenResponse = await getVoiceCoachWsToken();
          if (!sessionActiveRef.current || sessionRunIdRef.current !== startupRunId) {
            return;
          }
          const sessionToken = tokenResponse?.data?.sessionToken;
          if (sessionToken) {
            await connectWs(sessionToken);
          } else {
            if (!sessionActiveRef.current || sessionRunIdRef.current !== startupRunId) {
              return;
            }
            emitTtsTrace('start_missing_ws_token', {
              transport: 'http',
              fallbackReason: 'missing_ws_token',
            });
            setTransport({ mode: 'http', fallbackReason: 'missing_ws_token' });
          }
        } catch (backgroundErr) {
          if (!sessionActiveRef.current || sessionRunIdRef.current !== startupRunId) {
            return;
          }
          emitTtsTrace('background_start_failed', {
            transport: 'http',
            fallbackReason: 'background_start_failed',
            errorName: backgroundErr?.name || null,
            errorMessage: backgroundErr?.message || String(backgroundErr),
          });
          setTransport({ mode: 'http', fallbackReason: 'background_start_failed' });
        }
      })();
    } catch (err) {
      console.error('[AICoachOpenMic] startSession failed', err);
      emitTtsTrace('start_session_failed', {
        transport: 'ws',
        errorName: err?.name || null,
        errorCode: err?.code || null,
        errorStatus: err?.response?.status || null,
        errorMessage: err?.message || String(err),
        micDiag: err?.diag || null,
      });

      // Ensure partially started sessions do not leak sockets/VAD/audio state.
      stopPlayback();
      stopNativePlayback();
      await stopVAD();
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch {
          // ignore
        }
        wsRef.current = null;
      }

      sessionActiveRef.current = false;
      sessionStartingRef.current = false;
      vadReadyRef.current = false;
      setSessionActive(false);
      setSessionStarting(false);
      setConnectionStatus('disconnected');
      setPhase('idle');
      serverPhaseRef.current = 'idle';

      if (err?.response?.status === 429) {
        markLimitReached();
        return;
      }

      const errorName = String(err?.name || '').toLowerCase();
      const errorMessage = String(err?.message || '').toLowerCase();
      if (err?.code === 'MIC_API_UNAVAILABLE') {
        const diag = err?.diag || getMicApiDiagnostics();
        emitTtsTrace('mic_api_unavailable', {
          transport: 'ws',
          ...diag,
        });
        const isHttpInNativeWebView = Boolean(isNativeApp && diag?.protocol === 'http:');
        if (isHttpInNativeWebView) {
          setError(t('aiCoach.micUnavailableHttp', 'Voice Coach mic is unavailable on insecure HTTP in iOS WebView. Use HTTPS for EXPO_PUBLIC_WEB_BASE_URL and rebuild the dev client.'));
        } else {
          setError(t('aiCoach.micUnavailableGeneric', 'Microphone API unavailable in this environment. Open Forza in a secure HTTPS context and retry.'));
        }
      } else if (
        errorName.includes('notallowederror')
        || errorName.includes('permissiondeniederror')
        || errorMessage.includes('permission denied')
        || errorMessage.includes('not allowed')
      ) {
        setError(t('aiCoach.micPermissionDenied', 'Microphone permission denied. Enable microphone access and try again.'));
      } else if (
        errorName.includes('notfounderror')
        || errorName.includes('notreadableerror')
        || errorMessage.includes('no microphone')
        || errorMessage.includes('audio input')
      ) {
        setError(t('aiCoach.micNotFound', 'No usable microphone was found. Check iPhone mic permissions and try again.'));
      } else {
        setError(t('aiCoach.sessionStartFailed', 'Unable to start voice session.'));
      }
    }
  }, [
    connectWs,
    ensureAudioContext,
    resolveSessionTtsMode,
    isNativeApp,
    markLimitReached,
    onDeviceVoicePreferred,
    requestCaps,
    nativeCaps?.kokoroTts,
    postToNative,
    refreshCoachUsage,
    startVAD,
    stopNativePlayback,
    stopPlayback,
    stopVAD,
    emitTtsTrace,
    t,
  ]);

  useEffect(() => {
    if (!firstTimeLocalSetupActive || !kokoroReady) {
      return;
    }
    requestCaps();
    emitTtsTrace('voice_choice_local_init_ready_waiting_prewarm', {
      transport: 'ws',
      selectedMode: pendingVoiceModeStartRef.current || 'native_text',
    });
  }, [emitTtsTrace, firstTimeLocalSetupActive, kokoroReady, requestCaps]);

  useEffect(() => {
    if (
      !isFirstTimeLocalSetupReady({
        firstTimeLocalSetupActive,
        kokoroReady,
        kokoroWarm,
      })
    ) {
      return;
    }
    persistOnDeviceVoicePreference(true);
    requestCaps();
    setLocalVoiceSetupPhase('ready');
    emitTtsTrace('voice_choice_local_prewarm_ready', {
      transport: 'ws',
      selectedMode: pendingVoiceModeStartRef.current || 'native_text',
    });
  }, [
    emitTtsTrace,
    firstTimeLocalSetupActive,
    kokoroReady,
    kokoroWarm,
    persistOnDeviceVoicePreference,
    requestCaps,
  ]);

  useEffect(() => {
    if (!firstTimeLocalSetupActive || kokoroError !== 'user_skipped') {
      return;
    }
    setLocalVoiceSetupPhase('idle');
    kokoroInitRequestedRef.current = false;
    const nextMode = 'server_pcm';
    pendingVoiceModeStartRef.current = null;
    emitTtsTrace('voice_choice_local_skipped_starting_cloud', {
      transport: 'ws',
      selectedMode: nextMode,
    });
    void startSession(nextMode);
  }, [emitTtsTrace, firstTimeLocalSetupActive, kokoroError, startSession]);

  useEffect(() => {
    return () => {
      if (nativeUnloadTimerRef.current) {
        clearTimeout(nativeUnloadTimerRef.current);
        nativeUnloadTimerRef.current = null;
      }
    };
  }, []);

  const handleWatchAds = useCallback(async () => {
    if (isUnlocking) return;
    setError(null);

    const showRewardedAd = window?.forzaNative?.showRewardedAd;
    if (typeof showRewardedAd !== 'function') {
      setError(t('aiCoach.rewardedAdsNativeOnly', 'Rewarded ads are available in the iOS app.'));
      return;
    }

    setIsUnlocking(true);
    try {
      showRewardedAd();

      const startedAt = Date.now();
      const timeoutMs = 90_000;
      const intervalMs = 1500;

      while (Date.now() - startedAt < timeoutMs) {
        const latest = await refreshCoachUsage();
        const usageSeconds = latest?.usageSeconds;
        if (typeof usageSeconds === 'number' && usageSeconds < 0) {
          setError(null);
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
      }
      setError(t('aiCoach.unlockPendingVerification', 'Bonus minute is almost ready. Waiting for verification...'));
    } finally {
      setIsUnlocking(false);
    }
  }, [isUnlocking, refreshCoachUsage, t]);

  const handleChooseOnDeviceVoice = useCallback(() => {
    if (!isNativeApp) {
      return;
    }
    const shouldGateSetup = shouldGateFirstTimeLocalSetup({
      isNativeApp,
      selectedMode: 'native_text',
      assetsInstalledAtSelection: kokoroAssetsInstalled,
    });
    setShowVoiceModeModal(false);
    setError(null);
    setLocalVoiceSetupPhase(shouldGateSetup ? 'installing' : 'idle');
    pendingVoiceModeStartRef.current = 'native_text';
    kokoroInitRequestedRef.current = true;
    emitTtsTrace('voice_choice_selected', {
      transport: 'ws',
      selectedMode: 'native_text',
      assetsInstalled: kokoroAssetsInstalled,
      firstTimeSetupGate: shouldGateSetup,
    });
    postToNative({
      type: 'FORZA_KOKORO_INIT',
      voice: 'af_heart',
      language: 'en-US',
    });
  }, [emitTtsTrace, isNativeApp, kokoroAssetsInstalled, postToNative]);

  const handleChooseCloudVoice = useCallback(() => {
    setShowVoiceModeModal(false);
    setError(null);
    setLocalVoiceSetupPhase('idle');
    pendingVoiceModeStartRef.current = null;
    emitTtsTrace('voice_choice_selected', {
      transport: 'ws',
      selectedMode: 'server_pcm',
      assetsInstalled: kokoroAssetsInstalled,
    });
    void startSession('server_pcm');
  }, [emitTtsTrace, kokoroAssetsInstalled, startSession]);

  const handleStartPreparedLocalVoice = useCallback(() => {
    const nextMode = pendingVoiceModeStartRef.current || 'native_text';
    pendingVoiceModeStartRef.current = null;
    setLocalVoiceSetupPhase('idle');
    setError(null);
    emitTtsTrace('voice_choice_local_ready_manual_start', {
      transport: 'ws',
      selectedMode: nextMode,
    });
    void startSession(nextMode);
  }, [emitTtsTrace, startSession]);

  const handleUseCloudVoiceAfterLocalSetup = useCallback(() => {
    pendingVoiceModeStartRef.current = null;
    setLocalVoiceSetupPhase('idle');
    setError(null);
    emitTtsTrace('voice_choice_local_ready_starting_cloud', {
      transport: 'ws',
      selectedMode: 'server_pcm',
    });
    void startSession('server_pcm');
  }, [emitTtsTrace, startSession]);

  const handleDismissLocalVoiceReady = useCallback(() => {
    pendingVoiceModeStartRef.current = null;
    setLocalVoiceSetupPhase('idle');
    emitTtsTrace('voice_choice_local_ready_dismissed', {
      transport: 'ws',
    });
  }, [emitTtsTrace]);

  const toggleSession = useCallback(() => {
    if (sessionActiveRef.current || sessionStartingRef.current) {
      void stopSession();
      return;
    }
    if (localVoiceSetupPhase !== 'idle') {
      return;
    }
    if (shouldPromptForInitialVoiceChoice) {
      setError(null);
      setShowVoiceModeModal(true);
      requestCaps();
      emitTtsTrace('voice_choice_prompted', {
        transport: 'ws',
        assetsInstalled: kokoroAssetsInstalled,
      });
      return;
    }
    void startSession();
  }, [
    emitTtsTrace,
    localVoiceSetupPhase,
    kokoroAssetsInstalled,
    requestCaps,
    shouldPromptForInitialVoiceChoice,
    startSession,
    stopSession,
  ]);

  // Cleanup only on unmount - use ref to avoid cleanup on callback recreation
  useEffect(() => {
    return () => {
      if (nativeSpeakGuardTimerRef.current) {
        clearTimeout(nativeSpeakGuardTimerRef.current);
        nativeSpeakGuardTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      void stopSessionRef.current?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const disabled = coachLimitReached || isLoadingUsage || firstTimeLocalSetupActive || firstTimeLocalSetupReady;
  const nativeSpeaking = ttsModeRef.current === 'native_text' && (kokoroPlaybackActive || nativeSpeakRequestedRef.current);
  const displayPhase = isSpeaking || nativeSpeaking ? 'speaking' : phase;
  const sessionBusy = sessionActive || sessionStarting;
  const buttonBusy = sessionBusy || firstTimeLocalSetupActive;
  const phaseLabels = useMemo(() => ({
    idle: '',
    starting: t('aiCoach.phaseStarting', 'Connecting…'),
    listening: t('aiCoach.phaseListening', 'Listening…'),
    transcribing: t('aiCoach.phaseTranscribing', 'Transcribing…'),
    thinking: t('aiCoach.phaseThinking', 'Thinking…'),
    speaking: t('aiCoach.phaseSpeaking', 'Speaking…'),
  }), [t]);
  const statusText = firstTimeLocalSetupActive
    ? t('aiCoach.statusPreparingOnDeviceVoice', 'Preparing on-device voice…')
    : firstTimeLocalSetupReady
      ? t('aiCoach.statusOnDeviceVoiceReady', 'On-device voice is ready')
      : sessionBusy
        ? phaseLabels[displayPhase] || t('aiCoach.phaseListening', 'Listening…')
        : t('rewardedUnlock.voiceCoach.headline', 'Tap to start your Forza AI Coach');
  const unlockCopy = buildRewardedUnlockCopy(REWARDED_UNLOCK_CONTEXTS.VOICE_COACH, rewardedAds, t);
  const cloudVoiceDescription = subscriptionPlan === 'free-tier'
    ? t('aiCoach.cloudVoiceDescriptionFree', 'Fastest start. Free users may still need ad unlocks for short sessions.')
    : t('aiCoach.cloudVoiceDescriptionPaid', 'Fastest start. Uses cloud voice instead of the local engine.');
  const ttsLabel =
    ttsModeRef.current === 'native_text'
      ? kokoroReady
        ? t('aiCoach.onDeviceVoiceReadyLabel', 'On-device voice')
        : t('aiCoach.onDeviceVoiceWarmingLabel', 'On-device voice (warming…)')
      : t('aiCoach.cloudVoiceLabel', 'Cloud voice');

  if (coachLimitReached && subscriptionPlan === 'free-tier') {
    return (
      <div className="w-full mt-4 rounded-[2rem] border border-cyan-400/25 bg-slate-950/80 px-6 py-6 text-center shadow-2xl shadow-cyan-950/35 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-xs flex-col items-center">
          <Soundwave isActive={isUnlocking} />
          <h4 className="mt-2 text-lg font-black tracking-normal text-white">
            {unlockCopy.headline}
          </h4>

          {unlockCopy.progressLabel && (
            <div className="mt-2 inline-flex items-center justify-center rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-bold text-slate-200">
              {unlockCopy.progressLabel}
            </div>
          )}

          <div className="mt-6 grid w-full gap-3">
            <a
              href="/subscription"
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-cyan-300 px-5 py-4 text-base font-black text-slate-950 shadow-lg shadow-cyan-500/20 transition hover:bg-cyan-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
            >
              <Crown className="h-5 w-5" />
              {unlockCopy.upgradeLine}
            </a>

            {isNativeApp ? (
              <button
                type="button"
                onClick={handleWatchAds}
                disabled={isUnlocking}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-cyan-300/35 bg-cyan-300/10 px-5 py-3.5 text-sm font-bold text-cyan-100 transition hover:border-cyan-200 hover:bg-cyan-300/15 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Play className="h-4 w-4 fill-current" />
                {isUnlocking ? t('aiCoach.openingUnlock', 'Opening unlock...') : unlockCopy.buttonLabel}
              </button>
            ) : (
              <a
                href="forza://open?path=/dashboard"
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-cyan-300/35 bg-cyan-300/10 px-5 py-3.5 text-sm font-bold text-cyan-100 transition hover:border-cyan-200 hover:bg-cyan-300/15"
              >
                <Play className="h-4 w-4 fill-current" />
                {unlockCopy.fallbackMessage}
              </a>
            )}
          </div>

          {error && (
            <div className="mt-4 rounded-2xl border border-red-400/25 bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-100">
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full mt-4">
      <button
        className={`w-full flex flex-col items-center justify-center cursor-pointer group focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#18181B] rounded-lg ${disabled ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        onClick={toggleSession}
        disabled={disabled}
        title={coachLimitReached ? (subscriptionPlan === 'free-tier' ? unlockCopy.headline : t('aiCoach.sessionTitleLimit', 'Monthly AI Coach limit reached.')) : t('aiCoach.sessionTitle', 'Start AI Coach session')}
      >
        <Soundwave isActive={buttonBusy} />
        <p className="mt-2 text-gray-400 text-xs group-hover:text-white font-small text-center flex items-center transition-colors duration-300">
          {statusText}
        </p>
        {sessionBusy && (
          <p className="text-gray-400 text-sm group-hover:text-white transition-colors duration-300 font-small">
            {t('aiCoach.statusPrefix', 'Status:')} {phaseLabels[displayPhase] || t('aiCoach.phaseListening', 'Listening…')} {connectionStatus === 'connected' ? '' : t('aiCoach.offlineMode', '(offline: {mode})', { mode: transport.mode })}
          </p>
        )}
        {sessionActive && (
          <p className="text-gray-500 text-xs mt-1">
            {t('aiCoach.ttsPrefix', 'TTS:')} {ttsLabel}{kokoroError && kokoroError !== 'user_skipped' ? ` ${t('aiCoach.ttsError', '(error)')}` : ''}{nativeSpeakRequestedRef.current && kokoroPlaybackActive ? ` ${t('aiCoach.ttsPlaying', '(playing)')}` : ''}
          </p>
        )}
        {sessionActive && transport.mode !== 'ws' && (
          <p className="text-yellow-400 text-xs mt-1">
            {t('aiCoach.httpFallbackActive', 'HTTP fallback active')}
          </p>
        )}
      </button>

      {coachLimitReached && (
        <div className="mt-2 text-center">
          <div className="text-sm text-yellow-400 rounded-lg">
            {subscriptionPlan === 'free-tier' ? (
              <div className="space-y-2">
                <div>{unlockCopy.headline}</div>
                {unlockCopy.progressLabel && (
                  <div className="text-xs text-gray-300">
                    {unlockCopy.progressLabel}
                  </div>
                )}
                {isNativeApp ? (
                  <button
                    type="button"
                    onClick={handleWatchAds}
                    disabled={isUnlocking}
                    className="px-4 py-2 rounded-lg bg-cyan-500 text-black font-bold disabled:opacity-60"
                  >
                    {isUnlocking ? t('aiCoach.openingUnlock', 'Opening unlock...') : unlockCopy.buttonLabel}
                  </button>
                ) : (
                  <a
                    href="forza://open?path=/dashboard"
                    className="font-bold underline"
                  >
                    {unlockCopy.fallbackMessage}
                  </a>
                )}
                <div className="text-xs text-gray-300">
                  {t('aiCoach.upgradePrompt', 'Want full access?')} <a href="/subscription" className="font-bold underline">{t('aiCoach.upgradeCta', 'Upgrade to Ultra')}</a>
                </div>
              </div>
            ) : (
              <>
                {t('aiCoach.limitReachedBody', "You've reached your monthly limit.")}
                <a href="/subscription" className="font-bold underline"> {t('aiCoach.upgradeCta', 'Upgrade to Ultra')}</a>
              </>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="mt-2 text-center text-xs text-red-400">
          {error}
        </div>
      )}

      <Modal
        isOpen={showVoiceModeModal}
        onClose={() => setShowVoiceModeModal(false)}
        title={t('aiCoach.voiceModalTitle', 'Choose Your Voice')}
        size="md"
      >
        <div className="space-y-4 text-left">
          <div className="rounded-2xl border border-cyan-500/30 bg-cyan-500/10 p-4">
            <p className="text-sm font-semibold text-cyan-200">{t('aiCoach.voiceModalRecommendedTitle', 'Best for regular AI Coach use')}</p>
            <p className="mt-2 text-sm text-slate-200">
              {t('aiCoach.voiceModalRecommendedBody', 'Downloading the on-device voice is a one-time setup. After that, voice coaching lasts longer without repeated ad unlocks.')}
            </p>
          </div>

          <button
            type="button"
            onClick={handleChooseOnDeviceVoice}
            className="w-full rounded-2xl bg-gradient-to-r from-cyan-400 to-cyan-500 px-5 py-4 text-left text-slate-950 transition-transform hover:scale-[1.01]"
          >
            <span className="block text-sm font-semibold uppercase tracking-wide">{t('aiCoach.voiceModalRecommendedBadge', 'Recommended')}</span>
            <span className="mt-1 block text-lg font-bold">{t('aiCoach.voiceModalDownloadTitle', 'Download Voice To This iPhone')}</span>
            <span className="mt-1 block text-sm text-slate-900/80">
              {t('aiCoach.voiceModalDownloadBody', 'One-time setup, around 300 MB.')}
            </span>
          </button>

          <button
            type="button"
            onClick={handleChooseCloudVoice}
            className="w-full rounded-2xl border border-slate-700 bg-slate-900/70 px-5 py-4 text-left text-white transition-colors hover:border-cyan-400/60 hover:bg-slate-900"
          >
            <span className="block text-lg font-bold">{t('aiCoach.voiceModalCloudTitle', 'Use Cloud Voice Now')}</span>
            <span className="mt-1 block text-sm text-slate-300">
              {cloudVoiceDescription}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setShowVoiceModeModal(false)}
            className="w-full rounded-2xl border border-slate-800 px-5 py-3 text-sm font-semibold text-slate-300 transition-colors hover:border-slate-600 hover:text-white"
          >
            {t('aiCoach.voiceModalLater', 'Maybe later')}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={firstTimeLocalSetupReady}
        onClose={handleDismissLocalVoiceReady}
        title={t('aiCoach.readyModalTitle', 'On-Device Voice Ready')}
        size="md"
      >
        <div className="space-y-4 text-left">
          <div className="rounded-2xl border border-cyan-500/30 bg-cyan-500/10 p-4">
            <p className="text-sm font-semibold text-cyan-200">{t('aiCoach.readyModalHeadline', 'Longer voice coaching is now available')}</p>
            <p className="mt-2 text-sm text-slate-200">
              {t('aiCoach.readyModalBody', 'First-time setup is complete. Later on-device sessions should start much faster.')}
            </p>
          </div>

          <button
            type="button"
            onClick={handleStartPreparedLocalVoice}
            className="w-full rounded-2xl bg-gradient-to-r from-cyan-400 to-cyan-500 px-5 py-4 text-left text-slate-950 transition-transform hover:scale-[1.01]"
          >
            <span className="block text-lg font-bold">{t('aiCoach.readyModalStartTitle', 'Start Listening')}</span>
            <span className="mt-1 block text-sm text-slate-900/80">
              {t('aiCoach.readyModalStartBody', 'Begin your first on-device voice session now.')}
            </span>
          </button>

          <button
            type="button"
            onClick={handleUseCloudVoiceAfterLocalSetup}
            className="w-full rounded-2xl border border-slate-700 bg-slate-900/70 px-5 py-4 text-left text-white transition-colors hover:border-cyan-400/60 hover:bg-slate-900"
          >
            <span className="block text-lg font-bold">{t('aiCoach.readyModalCloudTitle', 'Use Cloud Voice Instead')}</span>
            <span className="mt-1 block text-sm text-slate-300">
              {t('aiCoach.readyModalCloudBody', 'Skip the local path for this session and start immediately with cloud voice.')}
            </span>
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default AICoachOpenMic;
